import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FileText, GitCompareArrows, Sparkles, Loader2, ExternalLink, Star, Copy, MessageCircle, Link2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { toast } from "sonner";

/**
 * Compact panel inside the Lead Detail "Tools" tab.
 * Shows every past MhM session for this lead with quick links to:
 *   • Clarity Report  (`/mhm/report/:sessionId`)
 *   • Manage Projects (`/mhm/projects/:sessionId`) — buyer self-serve add / score / re-compare
 *   • AI Project Fit  (`/mhm/comparison-report/:sessionId`)
 * Falls back to a friendly empty hint when the lead has no MhM sessions yet —
 * the existing <MhMFlow /> below this panel still lets the rep start one.
 */
const LeadMhMSessionsPanel = ({ apiBase, leadId, leadName, leadPhone }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const shareLink = leadId ? `${window.location.origin}/mhm/start?lead=${leadId}` : "";

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("MhM link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const whatsappShare = () => {
    const greeting = leadName ? `Hi ${leadName.split(" ")[0]},` : "Hi,";
    const message = `${greeting}\n\nTo help us find the right home for you, please complete this 5-minute MyHomeMatrix exercise — it captures what truly matters to you in a home:\n\n${shareLink}\n\nThanks!`;
    const phoneClean = (leadPhone || "").replace(/\D/g, "");
    const url = phoneClean
      ? `https://wa.me/${phoneClean}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  useEffect(() => {
    if (!apiBase || !leadId) return;
    (async () => {
      try {
        const r = await axios.get(`${apiBase}/mhm/sessions/lead/${leadId}`);
        setSessions(Array.isArray(r.data) ? r.data : []);
      } catch {
        setSessions([]);
      } finally { setLoading(false); }
    })();
  }, [apiBase, leadId]);

  if (loading) {
    return (
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="p-4 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading MhM sessions...
        </CardContent>
      </Card>
    );
  }

  // Small reusable share block
  const ShareBlock = (
    <div className="rounded-xl border border-dashed border-[#87A96B] bg-[#F0F5EC] p-3" data-testid="lead-mhm-share-block">
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="w-4 h-4 text-[#5A7A4A]" />
        <p className="text-sm font-semibold text-[#2C3E50]">Personalised MhM link for this lead</p>
      </div>
      <p className="text-[11px] text-slate-500 mb-2 break-all font-mono bg-white border border-slate-200 rounded px-2 py-1">{shareLink}</p>
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="outline" onClick={copyShareLink} data-testid="lead-mhm-copy-link">
          <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
        </Button>
        <Button size="sm" className="bg-[#25D366] hover:bg-[#1DA851] text-white" onClick={whatsappShare} data-testid="lead-mhm-whatsapp-share">
          <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp the lead
        </Button>
        <Button size="sm" variant="outline" onClick={() => window.open(shareLink, "_blank")} data-testid="lead-mhm-open-link">
          <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open as buyer
        </Button>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">The lead is pre-linked — their completed MhM will attach back to this CRM record automatically.</p>
    </div>
  );

  if (sessions.length === 0) {
    return (
      <Card className="bg-white border-slate-200 shadow-sm" data-testid="lead-mhm-sessions-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            MhM Sessions
            <Badge variant="outline" className="text-xs">None yet</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-3">
          <p className="text-sm text-slate-500">
            This lead hasn't completed a MyHomeMatrix assessment yet. Send them the personalised link below so they can fill it in their own time, or start one for them under "Start a new MhM session".
          </p>
          {leadId && ShareBlock}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="lead-mhm-sessions-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          MhM Sessions
          <Badge variant="outline" className="text-xs">{sessions.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-1 space-y-3">
        {leadId && ShareBlock}
        {sessions.map((s) => {
          const hasFingerprint = !!s.fingerprint;
          const dateStr = new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
          const sid = s.session_id;
          return (
            <div
              key={sid}
              className="border border-slate-200 rounded-xl p-3"
              data-testid={`lead-mhm-session-${sid}`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">
                    {s.persona_id ? s.persona_id.replace(/_/g, " ") : "Untitled session"}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {dateStr} · <span className="font-mono">{sid}</span>
                  </p>
                </div>
                <Badge className={hasFingerprint ? "bg-[#F0F5EC] text-[#5A7A4A] border-0" : "bg-amber-50 text-amber-700 border-0"}>
                  {hasFingerprint ? "Completed" : (s.status || "in progress")}
                </Badge>
              </div>

              <div className="flex flex-wrap gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasFingerprint}
                  onClick={() => window.open(`/mhm/report/${sid}`, "_blank")}
                  data-testid={`lead-mhm-open-clarity-${sid}`}
                >
                  <FileText className="w-3.5 h-3.5 mr-1" /> Clarity Report
                  <ExternalLink className="w-3 h-3 ml-1 opacity-60" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasFingerprint}
                  onClick={() => window.open(`/mhm/projects/${sid}`, "_blank")}
                  data-testid={`lead-mhm-manage-projects-${sid}`}
                >
                  <GitCompareArrows className="w-3.5 h-3.5 mr-1" /> Manage Projects
                  <ExternalLink className="w-3 h-3 ml-1 opacity-60" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasFingerprint}
                  onClick={() => window.open(`/mhm/comparison-report/${sid}`, "_blank")}
                  data-testid={`lead-mhm-fit-report-${sid}`}
                >
                  <Star className="w-3.5 h-3.5 mr-1" /> AI Project Fit
                  <ExternalLink className="w-3 h-3 ml-1 opacity-60" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(`/mhm/next/${sid}`, "_blank")}
                  className="text-slate-500"
                  data-testid={`lead-mhm-next-${sid}`}
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> Buyer's choice page
                </Button>
              </div>
            </div>
          );
        })}
        <p className="text-[11px] text-slate-400 pt-1">
          Tip: share the buyer's choice-page link to let them re-enter — view their report, manage projects, or take a fresh MhM.
        </p>
      </CardContent>
    </Card>
  );
};

export default LeadMhMSessionsPanel;
